import React, { useEffect, useState } from "react";
import { Icon, Field, PrimaryButton } from "@/components/kit";
import { api } from "@/api";
import { useI18n } from "@/i18n";

const FIELDS = [
  ["company_name", "s_name"], ["address", "s_address"], ["phone", "s_phone"],
  ["email", "s_email"], ["website", "s_website"], ["vat", "s_vat"], ["iban", "s_iban"], ["logo_url", "s_logo_url"],
  ["cgv_url", "s_cgv_url"], ["gdpr_url", "s_gdpr_url"],
];

export default function Settings() {
  const { t } = useI18n();
  const [v, setV] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.getSettings().then((d) => setV(d.company || {})).catch((e) => setError(e.message)).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true); setError(""); setSaved(false);
    try { const d = await api.updateSettings(v); setV(d.company); setSaved(true); }
    catch (e) { setError(e.message); } finally { setSaving(false); }
  };

  const pickLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true); setError("");
    try {
      const out = await api.uploadLogo(file);
      setV((s) => ({ ...s, logo_url: out.logo_url }));
      setSaved(true);
    } catch (err) { setError(err.message); } finally { setUploading(false); }
  };

  return (
    <div className="mx-auto max-w-2xl px-5 py-8 pb-28">
      <h1 className="font-display text-4xl font-semibold text-ink">{t("s_title")}</h1>
      <p className="mb-6 mt-1 text-[14px] text-mute">{t("s_sub")}</p>

      {loading ? (
        <div className="flex justify-center py-20"><Icon name="loader" size={32} className="animate-spin text-brand" /></div>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-col items-center gap-3 rounded-2xl border border-line bg-white p-6">
            {v.logo_url ? (
              <img src={v.logo_url} alt="logo" className="h-24 max-w-[220px] object-contain" />
            ) : (
              <div className="flex h-24 w-32 items-center justify-center rounded-lg border border-line bg-paper">
                <Icon name="image" size={28} className="text-mute" />
              </div>
            )}
            <label data-testid="upload-logo" className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-brand px-4 py-2 text-[14px] font-semibold text-brand">
              {uploading ? <Icon name="loader" size={16} className="animate-spin" /> : <Icon name="upload" size={16} />}
              {t("s_upload_logo")}
              <input type="file" accept="image/*" className="hidden" onChange={pickLogo} />
            </label>
          </div>

          <div className="rounded-2xl border border-line bg-white p-5">
            {FIELDS.map(([key, lbl]) => (
              <Field key={key} label={t(lbl)} testId={`s-${key}`} value={v[key] ?? ""} onChange={(e) => setV((s) => ({ ...s, [key]: e.target.value }))} type={key === "email" ? "email" : "text"} />
            ))}
          </div>

          {error ? <p data-testid="settings-error" className="text-[14px] font-medium text-err">{error}</p> : null}
          {saved ? <p data-testid="settings-saved" className="text-[14px] font-medium text-ok">{t("s_saved")}</p> : null}
        </div>
      )}

      <div className="fixed bottom-0 left-0 right-0 border-t border-divider bg-white/95 backdrop-blur-md lg:left-64">
        <div className="mx-auto max-w-2xl px-5 py-3">
          <PrimaryButton testId="settings-save" title={t("s_save")} icon="save" onClick={save} loading={saving} />
        </div>
      </div>
    </div>
  );
}
